import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "PolymerMeltFiltrationApplications",
    relatedTopic: "polymer",
    hero: {
      categoryBadge: "Polymer Filtration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Polymer Melt Filtration",
      lead: "Engineered stainless steel screen packs, sintered laminates, and continuous changer media designed for high-viscosity virgin polymer melt extrusion, fiber spinning beams, cast film lines, and pelletizing systems.",
      secondaryCta: {
        label: "Polymer Filtration Hub",
        to: "/PolymerFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-15b8ef.png",
        alt: "Continuous hydraulic polymer melt screen changer assembly and filter pack",
      },
      cardNote: "Melt Screen Changer Media",
      cardLink: {
        label: "Technical Specs",
        to: "/PolymerMeltFiltrationApplicationsPage#specifications",
      },
      specs: [
        { label: "Filtration Rating", value: "3–250 µm" },
        { label: "Alloy Metallurgy", value: "AISI 304L / 316L" },
        { label: "Form Factor", value: "Packs, discs & belt rolls" },
        { label: "Weave Styles", value: "Dutch & sintered laminates" },
      ],
    },
    overview: {
      eyebrow: "Severe Viscosity Melt Filtration",
      heading: "Continuous Gel and Particulate Interception in Polymer Melt Streams",
      paragraphs: [
        "In continuous polymer melt processing, raw resins and masterbatches introduce unplasticized gels, carbon specks, degraded polymer crosslinks, and trace particulate matter. Under high differential pressures, conventional single-layer screens risk pore expansion, wire displacement, and catastrophic blowout that can foul precision downstream extrusion tooling.",
        "MutualFilter provides purpose-built filtration media crafted from high-grade AISI 304L and 316L stainless steel woven wire cloth and diffusion-bonded sintered laminates. Calibrated plain Dutch weaves, twilled Dutch weaves, and multi-layer sintered composites offer stable pore geometry, uniform permeability, and high mechanical rigidity.",
        "Whether operating slide-plate, continuous hydraulic double-piston screen changers, or automated rotary belt systems, our spot-welded packs, aluminum-rimmed discs, and continuous coils integrate seamlessly with OEM breaker plates to protect downstream spinnerets, cast film dies, and underwater pelletizers.",
      ],
      keyPoints: [
        "Rigid stainless steel construction prevents wire movement and blowout under sustained differential pressures in heavy polymer service.",
        "Calibrated plain Dutch, twilled Dutch, and sintered laminates protect downstream spinnerets, cast film dies, and pelletizers from gel fouling and carbon specks.",
        "Custom framed packs, multi-layer spot-welded sets, and continuous screen changer belts engineered to precise OEM equipment tolerances.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/polymer_extrusion_filter.png",
        alt: "Stainless steel polymer melt extrusion multi-layer filter screen pack",
      },
    },
    specTable: {
      heading: "Polymer Melt Filter Media Configurations",
      intro: "Standard media types and layer configurations applied in industrial polymer melt filtration systems. Custom combinations and dimensions manufactured to specification.",
      columns: [
        { key: "configuration", label: "Media Construction", align: "left" },
        { key: "filtrationRating", label: "Filtration Rating", align: "right" },
        { key: "structure", label: "Structural Arrangement", align: "left" },
        { key: "commonFormats", label: "Available Form Factors", align: "left" },
        { key: "primaryUse", label: "Primary Application", align: "left" },
      ],
      rows: [
        {
          id: "pm-fine-dutch",
          configuration: "Twilled Dutch Weave",
          filtrationRating: "3–25 µm",
          structure: "Calibrated micro-aperture woven cloth",
          commonFormats: "Spot-welded packs, rimmed discs",
          primaryUse: "Synthetic fiber spinning, BOPP / cast film",
        },
        {
          id: "pm-plain-dutch",
          configuration: "Plain Dutch Weave",
          filtrationRating: "20–100 µm",
          structure: "High-density warp & shute wire weave",
          commonFormats: "Discs, auto-screen continuous belts",
          primaryUse: "Sheet extrusion, compounding lines",
        },
        {
          id: "pm-sintered-pack",
          configuration: "5-Layer Sintered Laminate",
          filtrationRating: "5–100 µm",
          structure: "Diffusion-bonded protective & support layers",
          commonFormats: "Framed discs, candle elements",
          primaryUse: "Continuous high-differential-pressure lines",
        },
        {
          id: "pm-multi-stack",
          configuration: "Graduated Multi-Layer Pack",
          filtrationRating: "25–250 µm",
          structure: "Coarse mesh support + Dutch control layer",
          commonFormats: "Aluminum-rimmed discs, kidney packs",
          primaryUse: "Hydraulic slide-plate screen changers",
        },
      ],
      caption: "Note: Custom alloy selections (AISI 304, 304L, 316, 316L) and layer counts tailored to breaker plate dimensions and resin characteristics.",
    },
    features: {
      heading: "Engineering Capabilities for Polymer Melt Processing",
      intro: "MutualFilter delivers precision-fabricated filtration media designed for demanding melt rheology, continuous operating cycles, and zero polymer leakage.",
      items: [
        {
          title: "Zero Wire Migration",
          text: "Diffusion bonding and precision spot-welding lock every wire crossover in position, preventing wire detachment and media deformation into breaker plate holes.",
        },
        {
          title: "Predictable Micron Cut-Off",
          text: "Calibrated pore openings ensure defined particulate and gel retention across the entire face area without sudden differential pressure spikes.",
        },
        {
          title: "Rigid Edge Sealing",
          text: "Sturdy aluminum and copper U-channel rims or clean cut edges guarantee flush seating against changer cavities, eliminating polymer bypass.",
        },
        {
          title: "OEM Dimensional Conformity",
          text: "Manufactured to exact outer diameters, kidney profiles, and continuous belt widths for seamless compatibility with leading extrusion machinery.",
        },
      ],
    },
    related: {
      heading: "Related Polymer Pages & Recommended Products",
      intro: "Explore complementary polymer filtration processes, wire mesh configurations, and fabricated screen assemblies.",
      cards: [
        {
          title: "Plastic Recycling Filtration",
          text: "Continuous melt filtration media engineered for contaminated post-consumer and post-industrial recycled plastics and pelletizing lines.",
          to: "/PlasticRecyclingFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Continuous melt filter screen and extrusion breaker plate assembly",
          },
          badge: "Sibling Application",
          linkLabel: "View Recycling Solutions",
        },
        {
          title: "Polymer Filtration Hub",
          text: "Comprehensive filtration solutions for virgin resin extrusion, synthetic fiber spinning, blown film, and compounding systems.",
          to: "/PolymerFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-polymer-ext-65ec06.png",
            alt: "Polymer extrusion multi-layer filter screen pack",
          },
          badge: "Parent Application",
          linkLabel: "View Polymer Hub",
        },
        {
          title: "Screen Packs",
          text: "Spot-welded and aluminum-rimmed multi-layer wire mesh packs configured for continuous hydraulic screen changers and high head pressures.",
          to: "/ScreenPacksPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Stainless steel multi-layer screen pack for polymer extrusion",
          },
          badge: "Recommended Product",
          linkLabel: "View Screen Packs",
        },
        {
          title: "Woven Wire Mesh",
          text: "Calibrated plain Dutch, twilled Dutch, and square weave stainless steel cloth delivering defined particle cut-off and high mechanical strength.",
          to: "/WovenWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Plain Dutch weave stainless steel wire cloth for melt filtration",
          },
          badge: "Recommended Product",
          linkLabel: "View Woven Mesh",
        },
        {
          title: "Sintered Wire Mesh",
          text: "Rigid diffusion-bonded multi-layer laminates with zero wire migration under cyclic thermal stress, ideal for high-pressure melt filter discs.",
          to: "/SinteredWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Multilayer sintered wire mesh laminate disc for melt filtration",
          },
          badge: "Recommended Product",
          linkLabel: "View Sintered Mesh",
        },
        {
          title: "Filter Components",
          text: "Fabricated stainless steel discs, cylindrical elements, and custom pack assemblies manufactured to OEM extruder specifications.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Fabricated stainless steel filter components and screen assemblies",
          },
          badge: "Product Category",
          linkLabel: "View Filter Components",
        },
      ],
    },
    cta: {
      id: "polymer-melt-quote",
      eyebrow: "Polymer Melt Filter Media Engineering",
      heading: "Request a Technical Quote for Polymer Melt Filter Media",
      lead: "Provide your resin type, target filtration rating, changer model, and pack geometry. Our engineering team will recommend the optimal mesh structure and layer configuration.",
      secondaryLabel: "Explore Polymer Filtration",
      secondaryTo: "/PolymerFiltrationPage",
      note: "Material test reports (MTR) and PMI alloy verification available upon request with your order.",
    },
  },
  zh: {
    breadcrumbKey: "PolymerMeltFiltrationApplications",
    relatedTopic: "polymer",
    hero: {
      categoryBadge: "聚合物过滤",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "聚合物熔体过滤",
      lead: "工程级不锈钢丝网组合网包、烧结复合网板与连续走网滤带，专为原生聚合物高粘度熔体挤出、化纤纺丝箱体、流延薄膜及水下切粒造粒系统量身定制。",
      secondaryCta: {
        label: "聚合物过滤总览",
        to: "/PolymerFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-15b8ef.png",
        alt: "连续液压聚合物熔体换网器组件与滤网总成",
      },
      cardNote: "熔体换网器专用滤材",
      cardLink: {
        label: "技术规格",
        to: "/PolymerMeltFiltrationApplicationsPage#specifications",
      },
      specs: [
        { label: "过滤精度", value: "3–250 µm" },
        { label: "标准合金材质", value: "AISI 304L / 316L" },
        { label: "交付形态", value: "网包、圆片与连续网带" },
        { label: "织造形式", value: "密纹网与烧结复合板" },
      ],
    },
    overview: {
      eyebrow: "高粘度熔体精密过滤",
      heading: "连续聚合物熔体流中的凝胶与杂质颗粒稳定拦截",
      paragraphs: [
        "在连续聚合物熔融加工过程中，原材料树脂与母粒易产生塑化不良凝胶、碳化黑点、交联热降解物及外来微细杂质。在高压差工作条件下，传统单层丝网容易产生网孔受压形变、网丝移位甚至穿孔破损，进而污染下游昂贵的精密模具。",
        "互拓（MutualFilter）提供专为熔体工况打造的不锈钢编织网与扩散烧结复合滤材，选用优质 AISI 304L 与 316L 材质。校准级平纹荷兰编织网、斜纹荷兰编织网及多层扩散烧结板具有稳固的微孔通道、均匀的透流性能与极高的抗形变刚性。",
        "无论是板式滑块换网器、连续双柱柱塞换网器还是自动化连续走网滤带系统，我们的点焊组合网包、铝合金包边网盘与成卷网带均能严密贴合多孔分流板，有效保护下游喷丝板、流延膜头与切粒机组件。",
      ],
      keyPoints: [
        "刚性不锈钢组合结构在高压差持续载荷下杜绝滤层移位与破损穿孔，保障高粘度熔体运行稳定。",
        "校准级平纹荷兰网、斜纹荷兰网及烧结复合材料精密拦截凝胶与碳化物，保护喷丝板与挤出模头。",
        "支持定制包边网包、多层点焊滤组及自动换网器走网滤带，精准匹配 OEM 设备公差并杜绝熔体旁漏。",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/polymer_extrusion_filter.png",
        alt: "不锈钢聚合物熔体挤出多层组合滤网片",
      },
    },
    specTable: {
      heading: "聚合物熔体过滤介质配置体系",
      intro: "工业聚合物熔体过滤系统中常用的滤材类型与多层组合方案。所有规格与几何尺寸均可按图纸要求定制加工。",
      columns: [
        { key: "configuration", label: "滤材结构类型", align: "left" },
        { key: "filtrationRating", label: "名义过滤精度", align: "right" },
        { key: "structure", label: "结构组成", align: "left" },
        { key: "commonFormats", label: "常见交付形式", align: "left" },
        { key: "primaryUse", label: "主要应用场景", align: "left" },
      ],
      rows: [
        {
          id: "pm-fine-dutch-zh",
          configuration: "斜纹荷兰编织网 (Twilled Dutch)",
          filtrationRating: "3–25 µm",
          structure: "精密校准微孔编织丝网",
          commonFormats: "多点点焊网包、冲压包边圆片",
          primaryUse: "合成纤维纺丝、BOPP/流延膜挤出",
        },
        {
          id: "pm-plain-dutch-zh",
          configuration: "平纹荷兰编织网 (Plain Dutch)",
          filtrationRating: "20–100 µm",
          structure: "高密度经纬交织致密滤网",
          commonFormats: "圆形网片、连续自动换网走网带",
          primaryUse: "板片材挤出、共混造粒母料生产",
        },
        {
          id: "pm-sintered-pack-zh",
          configuration: "五层扩散烧结复合网 (5-Layer)",
          filtrationRating: "5–100 µm",
          structure: "真空扩散焊接保护层与多层骨架",
          commonFormats: "带框烧结圆盘、烛式金属滤芯",
          primaryUse: "持续高压差、频繁反冲洗挤出线",
        },
        {
          id: "pm-multi-stack-zh",
          configuration: "阶梯递进多层组合网包",
          filtrationRating: "25–250 µm",
          structure: "粗方孔支撑网 + 细荷兰截留层",
          commonFormats: "铝合金U型包边圆片、腰形网包",
          primaryUse: "液压滑板式与柱式熔体换网器",
        },
      ],
      caption: "注：支持 AISI 304、304L、316、316L 等奥氏体不锈钢牌号，层数搭配与外形尺寸根据多孔板结构定制。",
    },
    features: {
      heading: "聚合物熔体工艺工程制造优势",
      intro: "互拓专注制造满足严苛熔体流变特性、长运行周期与无侧漏要求的精密过滤介质。",
      items: [
        {
          title: "零丝径位移与抗吹破",
          text: "通过真空扩散焊接或精密数控电阻点焊固定各丝网节点，承受熔体背压冲击时不产生位移或凹陷嵌孔。",
        },
        {
          title: "可预测的微米截留精度",
          text: "校准级编织孔径确保全截面均匀截留凝胶与固体颗粒，防止局部流阻剧增或过早产生压差峰值。",
        },
        {
          title: "严密边缘封装防跑料",
          text: "精密 U 型铝合金或紫铜冲压包边，严实密封切边丝头，与换网器安装槽贴合紧密，消除熔体旁路泄漏。",
        },
        {
          title: "严格匹配 OEM 设备公差",
          text: "支持圆形、跑道形、肾形及连续自动走网带，外径与宽度公差精准，保障设备换网流畅利落。",
        },
      ],
    },
    related: {
      heading: "相关聚合物过滤页面与推荐产品",
      intro: "浏览互拓配套的塑料回收工艺方案、编织丝网品类及深加工滤网组件。",
      cards: [
        {
          title: "塑料回收过滤应用",
          text: "专为消费后与工业再生塑料造粒打造的连续换网过滤组件，有效应对高杂质负荷与熔体波动。",
          to: "/PlasticRecyclingFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "塑料回收连续熔体过滤网片与分流板组件",
          },
          badge: "同级应用",
          linkLabel: "查看塑料回收方案",
        },
        {
          title: "聚合物过滤应用中心",
          text: "涵盖树脂造粒、化纤纺丝、薄膜挤出及再生塑料全流程的不锈钢丝网综合过滤方案。",
          to: "/PolymerFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-polymer-ext-65ec06.png",
            alt: "聚合物过滤应用方案与多层滤网组合",
          },
          badge: "上级应用中心",
          linkLabel: "查看聚合物总览",
        },
        {
          title: "换网器组合滤网包 (Screen Packs)",
          text: "多层点焊与铝合金包边丝网组合滤片，专用于连续液压换网器与高机头挤出压力工况。",
          to: "/ScreenPacksPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "聚合物挤出用多层不锈钢换网滤片",
          },
          badge: "推荐产品",
          linkLabel: "查看组合网包",
        },
        {
          title: "不锈钢编织丝网",
          text: "高精度平纹荷兰网、斜纹荷兰网及方孔不锈钢网，提供清晰的截留界限与卓越机械强度。",
          to: "/WovenWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "熔体过滤用平纹荷兰编织不锈钢滤布",
          },
          badge: "推荐产品",
          linkLabel: "查看编织丝网",
        },
        {
          title: "多层烧结丝网",
          text: "刚性扩散焊接复合板，热循环应力下绝无断丝移位，是高压熔体过滤圆盘与滤芯的理想滤材。",
          to: "/SinteredWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "熔体过滤用多层烧结不锈钢网板与滤片",
          },
          badge: "推荐产品",
          linkLabel: "查看烧结丝网",
        },
        {
          title: "深加工过滤组件",
          text: "各类冲压滤圆片、圆筒滤网管及定制换网网包，按图纸要求精密制造。",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "互拓深加工不锈钢过滤组件系列",
          },
          badge: "产品分类",
          linkLabel: "查看全部过滤组件",
        },
      ],
    },
    cta: {
      id: "polymer-melt-quote",
      eyebrow: "聚合物熔体滤材定制配套",
      heading: "获取聚合物熔体过滤介质方案与商务报价",
      lead: "请提供您的树脂类别、目标截留精度、换网器规格及网片尺寸要求，我们的技术团队将为您推荐最适宜的层叠结构与选型方案。",
      secondaryLabel: "返回聚合物过滤中心",
      secondaryTo: "/PolymerFiltrationPage",
      note: "按订单要求可提供材质检验报告 (MTR) 与 PMI 光谱材质分析。",
    },
  },
  es: {
    breadcrumbKey: "PolymerMeltFiltrationApplications",
    relatedTopic: "polymer",
    hero: {
      categoryBadge: "Filtración de polímeros",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Filtración de polímeros fundidos",
      lead: "Paquetes filtrantes multicapa de acero inoxidable, laminados sinterizados y bandas continuas para cambiadores de malla diseñados para extrusión de fundido virgen de alta viscosidad, hilatura y peletizado.",
      secondaryCta: {
        label: "Centro de filtración de polímeros",
        to: "/PolymerFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-15b8ef.png",
        alt: "Conjunto de cambiador de filtros continuo para polímero fundido",
      },
      cardNote: "Medios para cambiadores de malla",
      cardLink: {
        label: "Especificaciones técnicas",
        to: "/PolymerMeltFiltrationApplicationsPage#specifications",
      },
      specs: [
        { label: "Grado de filtración", value: "3–250 µm" },
        { label: "Aleación estándar", value: "AISI 304L / 316L" },
        { label: "Formatos", value: "Packs, discos y bobinas" },
        { label: "Tipos de tejido", value: "Tejidos Dutch y laminados sinterizados" },
      ],
    },
    overview: {
      eyebrow: "Filtración de fundidos de alta viscosidad",
      heading: "Intercepción continua de geles y partículas en corrientes de polímero fundido",
      paragraphs: [
        "En la extrusión continua de polímeros, las resinas y masterbatches introducen geles no fundidos, motas de carbón y partículas degradadas. Bajo presiones diferenciales severas, las telas simples sufren deformación de poros y desplazamientos de alambres que pueden comprometer el herramental aguas abajo.",
        "MutualFilter suministra medios filtrantes fabricados en aceros inoxidables AISI 304L y 316L, combinando telas de tejido Dutch liso, Dutch cruzado y laminados sinterizados por difusión que garantizan rigidez estructural y permeabilidad constante.",
        "Nuestros paquetes soldados por puntos, discos ribeteados en aluminio y bobinas continuas encajan perfectamente en cambiadores hidráulicos de placas deslizantes, pistón doble y sistemas automáticos para proteger hileras, cabezales de film y peletizadores.",
      ],
      keyPoints: [
        "Construcción rígida en acero inoxidable que evita deformaciones y roturas bajo altas presiones diferenciales sostenidas.",
        "Tejidos Dutch lisos, cruzados y laminados sinterizados calibrados que protegen hileras, matrices de extrusión y peletizadores.",
        "Packs enmarcados a medida, conjuntos soldados por puntos y bandas continuas diseñadas según tolerancias OEM.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/polymer_extrusion_filter.png",
        alt: "Paquete de malla filtrante multicapa de acero inoxidable para extrusión de polímeros",
      },
    },
    specTable: {
      heading: "Configuraciones de medios filtrantes para polímeros",
      intro: "Combinaciones estándar de telas y mallas empleadas en filtración de polímero fundido. Diseños a medida fabricados según especificaciones.",
      columns: [
        { key: "configuration", label: "Construcción", align: "left" },
        { key: "filtrationRating", label: "Grado de filtración", align: "right" },
        { key: "structure", label: "Estructura", align: "left" },
        { key: "commonFormats", label: "Formatos comunes", align: "left" },
        { key: "primaryUse", label: "Aplicación principal", align: "left" },
      ],
      rows: [
        {
          id: "pm-fine-dutch-es",
          configuration: "Tejido Dutch cruzado",
          filtrationRating: "3–25 µm",
          structure: "Malla tejida con microaberturas calibradas",
          commonFormats: "Packs soldados, discos ribeteados",
          primaryUse: "Hilatura de fibras sintéticas, film BOPP",
        },
        {
          id: "pm-plain-dutch-es",
          configuration: "Tejido Dutch liso",
          filtrationRating: "20–100 µm",
          structure: "Trama compacta de alta densidad",
          commonFormats: "Discos, bobinas para cambiadores automáticos",
          primaryUse: "Extrusión de lámina, líneas de compounding",
        },
        {
          id: "pm-sintered-pack-es",
          configuration: "Laminado sinterizado de 5 capas",
          filtrationRating: "5–100 µm",
          structure: "Capas unidas por difusión térmica al vacío",
          commonFormats: "Discos enmarcados, bujías filtrantes",
          primaryUse: "Líneas de presión continua y retrolavado",
        },
        {
          id: "pm-multi-stack-es",
          configuration: "Pack multicapa graduado",
          filtrationRating: "25–250 µm",
          structure: "Malla soporte gruesa + capa fina de control",
          commonFormats: "Discos con marco de aluminio, mallas arriñonadas",
          primaryUse: "Cambiadores hidráulicos de placas deslizantes",
        },
      ],
      caption: "Nota: Opciones de aleación en AISI 304, 304L, 316 y 316L adaptadas a las dimensiones de la placa rompedora y la resina procesada.",
    },
    features: {
      heading: "Ventajas técnicas para extrusión de polímeros",
      intro: "Medios filtrantes de precisión desarrollados para alta reología de fusión, ciclos operativos continuos y cero fugas periféricas.",
      items: [
        {
          title: "Cero migración de alambre",
          text: "La unión por difusión o soldadura por puntos fija las intersecciones, evitando desprendimientos hacia los orificios de la placa rompedora.",
        },
        {
          title: "Punto de corte predecible",
          text: "Aberturas calibradas aseguran retención homogénea de partículas e impurezas sin provocar picos bruscos de presión diferencial.",
        },
        {
          title: "Sellado perimetral estanco",
          text: "Perfiles rígidos en U de aluminio o cobre crimpan los bordes y garantizan un asentamiento estanco que previene el bypass de fundido.",
        },
        {
          title: "Ajuste exacto a estándares OEM",
          text: "Tolerancias estrictas en diámetros exteriores, formas arriñonadas y anchos de banda para perfecta integración con maquinaria de extrusión.",
        },
      ],
    },
    related: {
      heading: "Páginas relacionadas y productos recomendados",
      intro: "Conozca procesos complementarios de polímeros y productos de malla de alambre inoxidable.",
      cards: [
        {
          title: "Filtración para reciclaje de plástico",
          text: "Medios para filtración continua de fundido en líneas de reprocesamiento y peletizado de plásticos reciclados.",
          to: "/PlasticRecyclingFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Malla de filtración de fundido y placa rompedora",
          },
          badge: "Aplicación hermana",
          linkLabel: "Ver reciclaje de plástico",
        },
        {
          title: "Centro de filtración de polímeros",
          text: "Soluciones completas de filtración para extrusión de polímeros, hilatura sintética, láminas y compounding.",
          to: "/PolymerFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-polymer-ext-65ec06.png",
            alt: "Paquete de malla multicapa para polímeros",
          },
          badge: "Centro principal",
          linkLabel: "Ver polímeros",
        },
        {
          title: "Paquetes de mallas filtrantes",
          text: "Packs soldados por puntos y con borde de aluminio diseñados para cambiadores hidráulicos continuos y altas presiones.",
          to: "/ScreenPacksPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Paquete de malla multicapa para extrusión de polímeros",
          },
          badge: "Producto recomendado",
          linkLabel: "Ver paquetes de mallas",
        },
        {
          title: "Malla de alambre tejida",
          text: "Tejidos Dutch lisos, cruzados y cuadrados de alta densidad con retención definida y máxima resistencia mecánica.",
          to: "/WovenWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Malla de alambre de tejido Dutch liso de acero inoxidable",
          },
          badge: "Producto recomendado",
          linkLabel: "Ver malla tejida",
        },
        {
          title: "Malla sinterizada",
          text: "Laminados sinterizados multicapa sin desprendimiento de alambres bajo tensión térmica y altas presiones de fundido.",
          to: "/SinteredWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disco laminado de malla sinterizada multicapa",
          },
          badge: "Producto recomendado",
          linkLabel: "Ver malla sinterizada",
        },
        {
          title: "Componentes filtrantes",
          text: "Discos estampados, cartuchos cilíndricos y ensamblajes a medida según tolerancias de maquinaria de extrusión.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Componentes filtrantes de acero inoxidable",
          },
          badge: "Categoría de producto",
          linkLabel: "Ver componentes",
        },
      ],
    },
    cta: {
      id: "polymer-melt-quote",
      eyebrow: "Ingeniería de medios filtrantes para fundido",
      heading: "Solicite cotización técnica para filtración de polímero fundido",
      lead: "Indique tipo de resina, retención requerida, modelo de cambiador de mallas y dimensiones para recibir una propuesta técnica a medida.",
      secondaryLabel: "Volver a filtración de polímeros",
      secondaryTo: "/PolymerFiltrationPage",
      note: "Informes de prueba de material (MTR) y análisis PMI disponibles bajo requerimiento con su pedido.",
    },
  },
  de: {
    breadcrumbKey: "PolymerMeltFiltrationApplications",
    relatedTopic: "polymer",
    hero: {
      categoryBadge: "Polymerfiltration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Polymerschmelzefiltration",
      lead: "Präzisionsgefertigte Mehrlagen-Siebpakete, Sintermetall-Laminate und Endlos-Siebbänder für die Extrusion hochviskoser Polymere, Chemiefaserspinnanlagen, Folienblasanlagen und Granuliersysteme.",
      secondaryCta: {
        label: "Übersicht Polymerfiltration",
        to: "/PolymerFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-15b8ef.png",
        alt: "Kontinuierlicher hydraulischer Siebwechsler für Polymerschmelzen",
      },
      cardNote: "Siebwechsler-Filtermedien",
      cardLink: {
        label: "Technische Daten",
        to: "/PolymerMeltFiltrationApplicationsPage#specifications",
      },
      specs: [
        { label: "Filterfeinheit", value: "3–250 µm" },
        { label: "Standardlegierung", value: "AISI 304L / 316L" },
        { label: "Lieferformen", value: "Pakete, Ronden & Endlosbänder" },
        { label: "Webarten", value: "Tressengewebe & Sinterlaminate" },
      ],
    },
    overview: {
      eyebrow: "Hochviskose Schmelzefiltration",
      heading: "Kontinuierliche Abscheidung von Gelen und Feststoffpartikeln im Schmelzestrom",
      paragraphs: [
        "Bei der kontinuierlichen Schmelzeaufbereitung führen unvollständig plastifizierte Gele, Verkokungsstippen und Abbauprodukte zu Qualitätsmängeln im Endprodukt. Unter anhaltenden Differenzdrücken drohen bei einfachen Geweben Maschenaufweitungen oder das Durchbrechen in die Lochplattenbohrungen.",
        "MutualFilter liefert metallische Filtermedien aus korrosionsbeständigen Edelstählen AISI 304L und 316L. Präzise Tressengewebe, Köpertressen und mehrlagige diffusionsgebundene Sinterlaminate bieten formstabile Porengeometrien und höchste mechanische Steifigkeit.",
        "Ob für hydraulische Schieber-Siebwechsler, Doppelkolbenwechsler oder automatische Endlos-Bandwechsler: Unsere punktgeschweißten Pakete, alugefassten Ronden und Rollen gewährleisten saubere Abdichtung und schützen Spinndüsen, Breitschlitzwerkzeuge und Granulatoren zuverlässig.",
      ],
      keyPoints: [
        "Starre Edelstahlkonstruktion verhindert Maschenverschiebung und Durchblasen bei hohen Differenzdrücken.",
        "Präzise Tressen-, Köpertressen- und Sinterverbunde schützen Spinndüsen, Breitschlitzwerkzeuge und Granulatoren.",
        "Gefasste Randsiebe, Punktschweißpakete und automatische Siebbänder nach OEM-Toleranzen.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/polymer_extrusion_filter.png",
        alt: "Mehrlagiges Edelstahl-Drahtgewebepaket für die Polymerschmelzeextrusion",
      },
    },
    specTable: {
      heading: "Konfigurationen für Polymerschmelze-Filtermedien",
      intro: "Gängige Filtergewebetypen und Mehrlagenaufbauten für industrielle Polymerextrusionsanlagen. Kundenspezifische Maße und Feinheiten nach Vorgabe.",
      columns: [
        { key: "configuration", label: "Medienaufbau", align: "left" },
        { key: "filtrationRating", label: "Filterfeinheit", align: "right" },
        { key: "structure", label: "Struktur", align: "left" },
        { key: "commonFormats", label: "Formate", align: "left" },
        { key: "primaryUse", label: "Haupteinsatzbereich", align: "left" },
      ],
      rows: [
        {
          id: "pm-fine-dutch-de",
          configuration: "Köpertressengewebe",
          filtrationRating: "3–25 µm",
          structure: "Kalibriertes Feinst-Drahtgewebe",
          commonFormats: "Punktgeschweißte Pakete, alugefasste Ronden",
          primaryUse: "Chemiefaserspinnen, BOPP- und Flachfolien",
        },
        {
          id: "pm-plain-dutch-de",
          configuration: "Glatte Tresse",
          filtrationRating: "20–100 µm",
          structure: "Dicht gewebtes Tressengefüge",
          commonFormats: "Rundscheiben, Endlos-Filterbänder",
          primaryUse: "Plattenextrusion, Compoundierung",
        },
        {
          id: "pm-sintered-pack-de",
          configuration: "5-lagiges Sinterlaminat",
          filtrationRating: "5–100 µm",
          structure: "Diffusionsgebundene Schutz- und Stützlagen",
          commonFormats: "Gefasste Scheiben, Filterkerzen",
          primaryUse: "Dauerbetrieb bei hohen Differenzdrücken",
        },
        {
          id: "pm-multi-stack-de",
          configuration: "Kaskadiertes Mehrlagenpaket",
          filtrationRating: "25–250 µm",
          structure: "Grobes Stützgitter + Tressen-Feinschicht",
          commonFormats: "Alugefasste Ronden, Nierenpakete",
          primaryUse: "Hydraulische Schieber-Siebwechsler",
        },
      ],
      caption: "Hinweis: Edelstahlwerkstoffe AISI 304, 304L, 316 und 316L; Schichtabfolgen abgestimmt auf Schmelzviskosität und Lochplattenmaße.",
    },
    features: {
      heading: "Konstruktionsmerkmale für Schmelzeanwendungen",
      intro: "Präzisionsfiltermedien entwickelt für anspruchsvolle Schmelzerheologie, kontinuierliche Betriebszeiten und leckagefreien Sitz.",
      items: [
        {
          title: "Keine Drahtverschiebung",
          text: "Diffusionsschweißung und Punktschweißverbindungen sichern die Drahtkreuzungspunkte gegen Verzug und Einpressen in Lochbohrungen.",
        },
        {
          title: "Definierte Abscheidegrenze",
          text: "Präzise Maschenweiten garantieren gleichmäßige Partikelrückhaltung ohne plötzlichen Anstieg des Schmelzedrucks.",
        },
        {
          title: "Sichere Kantenabdichtung",
          text: "Stabile Aluminium- oder Kupfer-U-Einfassungen umschließen Schnittkanten sauber und verhindern Schmelze-Bypass am Siebrand.",
        },
        {
          title: "Genaue OEM-Passform",
          text: "Einhaltung strenger Durchmesser-, Nieren- und Bandbreitentoleranzen für reibungslosen Betrieb gängiger Siebwechsler.",
        },
      ],
    },
    related: {
      heading: "Verwandte Seiten & empfohlene Produkte",
      intro: "Informieren Sie sich über weitere Polymerfiltrationsverfahren und montagefertige Edelstahl-Drahtgewebeprodukte.",
      cards: [
        {
          title: "Kunststoffrecycling-Filtration",
          text: "Schmelzefiltration für die kontinuierliche Aufbereitung von Post-Consumer- und Industrie-Rezyklaten.",
          to: "/PlasticRecyclingFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Industrielles Schmelzefiltersieb und Lochplatte",
          },
          badge: "Geschwister-Anwendung",
          linkLabel: "Recycling-Lösungen ansehen",
        },
        {
          title: "Polymerfiltration Übersicht",
          text: "Ganzheitliche Filtrationskonzepte für Neuharzextrusion, Faserspinnen, Blasfolien und Compoundierung.",
          to: "/PolymerFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-polymer-ext-65ec06.png",
            alt: "Edelstahl-Siebpaket für Polymerfiltration",
          },
          badge: "Hauptkategorie",
          linkLabel: "Polymerfiltration ansehen",
        },
        {
          title: "Siebpakete (Screen Packs)",
          text: "Punktgeschweißte und alugefasste Drahtgewebepakete für kontinuierliche Siebwechsler und hohe Drücke.",
          to: "/ScreenPacksPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Mehrlagiges Edelstahl-Siebpaket",
          },
          badge: "Empfohlenes Produkt",
          linkLabel: "Siebpakete ansehen",
        },
        {
          title: "Edelstahl-Drahtgewebe",
          text: "Präzise Tressen-, Köpertressen- und Quadratmaschengewebe mit definierter Trenngrenze und hoher Festigkeit.",
          to: "/WovenWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Glatte Tresse Edelstahldrahtgewebe",
          },
          badge: "Empfohlenes Produkt",
          linkLabel: "Drahtgewebe ansehen",
        },
        {
          title: "Sinterdrahtgewebe",
          text: "Starres diffusionsgebundenes Laminat ohne Drahtverschiebung unter thermischen Wechselbelastungen.",
          to: "/SinteredWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Mehrlagige Sinterscheibe für Schmelzefiltration",
          },
          badge: "Empfohlenes Produkt",
          linkLabel: "Sintergewebe ansehen",
        },
        {
          title: "Filterkomponenten",
          text: "Gestanzte Ronden, Zylinderfilter und OEM-Baugruppen nach Maß für Extrusionsanlagen.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Gefertigte Filterkomponenten aus Edelstahl",
          },
          badge: "Produktkategorie",
          linkLabel: "Komponenten ansehen",
        },
      ],
    },
    cta: {
      id: "polymer-melt-quote",
      eyebrow: "Individuelle Schmelzefiltermedien",
      heading: "Technisches Angebot für Schmelzefilter anfordern",
      lead: "Teilen Sie uns Polymertyp, gewünschte Filterfeinheit, Siebwechslermodell und Abmessungen mit für eine maßgeschneiderte Empfehlung.",
      secondaryLabel: "Zur Polymerfiltration",
      secondaryTo: "/PolymerFiltrationPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen auf Wunsch bei Bestellung erhältlich.",
    },
  },
  fr: {
    breadcrumbKey: "PolymerMeltFiltrationApplications",
    relatedTopic: "polymer",
    hero: {
      categoryBadge: "Filtration des polymères",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Filtration des polymères fondus",
      lead: "Packs de toiles métalliques inox, stratifiés frittés et toiles pour changeurs continus conçus pour l'extrusion de polymères fondus de haute viscosité, le filage textile, le film coulé et le compoundage.",
      secondaryCta: {
        label: "Pôle filtration des polymères",
        to: "/PolymerFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-15b8ef.png",
        alt: "Ensemble changeur de filtre hydraulique continu pour polymères fondus",
      },
      cardNote: "Milieux pour changeurs de toiles",
      cardLink: {
        label: "Spécifications techniques",
        to: "/PolymerMeltFiltrationApplicationsPage#specifications",
      },
      specs: [
        { label: "Seuil de filtration", value: "3–250 µm" },
        { label: "Nuance d'alliage", value: "AISI 304L / 316L" },
        { label: "Formes de livraison", value: "Packs, disques et bandes" },
        { label: "Types de tissage", value: "Tissages hollandais et toiles frittées" },
      ],
    },
    overview: {
      eyebrow: "Filtration des fondus à haute viscosité",
      heading: "Rétention continue des gels et particules dans les flux de polymère en fusion",
      paragraphs: [
        "Dans l'extrusion continue des polymères, les résines vierges et compounds contiennent des gels infondus, des grains de carbone et des résidus de dégradation thermique. Sous de fortes pressions différentielles, les toiles uniques risquent une déformation des mailles et un emboutissage dans les trous de la plaque perforée.",
        "MutualFilter fabrique des médias filtrants tout métal en acier inoxydable AISI 304L et 316L. Les toiles hollandaises unies, sergées et les stratifiés frittés multicouches offrent des seuils de rétention stables et une excellente rigidité mécanique.",
        "Qu'il s'agisse de changeurs de filtres hydrauliques à tiroirs, à doubles pistons ou de toiles défilantes automatiques, nos packs soudés par points, disques cerclés d'aluminium et bobines continues s'intègrent parfaitement aux plaques de rupture pour protéger filières de filage et têtes d'extrusion.",
      ],
      keyPoints: [
        "Structure rigide en acier inoxydable prévenant le glissement de fils et l'éclatement sous fortes pressions différentielles.",
        "Toiles hollandaises unies, sergées et stratifiés frittés protégeant filières de filage, têtes d'extrusion et granulateurs.",
        "Packs sertis sur mesure, empilages soudés par points et toiles pour changeurs continus selon tolérances OEM.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/polymer_extrusion_filter.png",
        alt: "Paquet de toiles filtrantes multicouches en acier inoxydable pour l'extrusion de polymères",
      },
    },
    specTable: {
      heading: "Configurations des toiles filtrantes pour polymères fondus",
      intro: "Types de toiles et empilements étagés couramment employés en filtration de polymères. Réalisations sur mesure selon plan.",
      columns: [
        { key: "configuration", label: "Construction", align: "left" },
        { key: "filtrationRating", label: "Seuil de filtration", align: "right" },
        { key: "structure", label: "Arrangement structurel", align: "left" },
        { key: "commonFormats", label: "Formes disponibles", align: "left" },
        { key: "primaryUse", label: "Application principale", align: "left" },
      ],
      rows: [
        {
          id: "pm-fine-dutch-fr",
          configuration: "Tissage hollandais sergé",
          filtrationRating: "3–25 µm",
          structure: "Toile tissée à micro-ouvertures calibrées",
          commonFormats: "Packs soudés, disques cerclés",
          primaryUse: "Filage textile synthétique, film BOPP coulé",
        },
        {
          id: "pm-plain-dutch-fr",
          configuration: "Tissage hollandais uni",
          filtrationRating: "20–100 µm",
          structure: "Toile repoussée de forte densité",
          commonFormats: "Disques, bandes pour changeurs automatiques",
          primaryUse: "Extrusion de plaques, compoundage",
        },
        {
          id: "pm-sintered-pack-fr",
          configuration: "Stratifié fritté 5 couches",
          filtrationRating: "5–100 µm",
          structure: "Liaisons par diffusion sous vide sans migration",
          commonFormats: "Disques cerclés, cartouches filtrantes",
          primaryUse: "Lignes sous fortes pressions différentielles",
        },
        {
          id: "pm-multi-stack-fr",
          configuration: "Pack multicouche étagé",
          filtrationRating: "25–250 µm",
          structure: "Toile de support grossière + couche fine",
          commonFormats: "Disques cerclés alu, formes haricot",
          primaryUse: "Changeurs hydrauliques à plaques coulissantes",
        },
      ],
      caption: "Note : Nuances AISI 304, 304L, 316 et 316L; empilements adaptés aux dimensions des plaques brise-jet et aux résines extrudées.",
    },
    features: {
      heading: "Atouts techniques pour l'extrusion des polymères",
      intro: "Médias filtrants de haute précision conçus pour les fortes contraintes rhéologiques, cycles continus et zéro fuite latérale.",
      items: [
        {
          title: "Zéro migration de fils",
          text: "Le frittage par diffusion et la soudure par points verrouillent les croisements de fils, évitant toute pénétration dans la plaque perforée.",
        },
        {
          title: "Seuil de coupure précis",
          text: "Pores rigoureusement calibrés pour une capture régulière des gels et impuretés sans montée abrupte de la pression différentielle.",
        },
        {
          title: "Étanchéité périphérique totale",
          text: "Cerclages en U en aluminium ou cuivre sertis évitant toute fuite de matière polymère autour de la plaque brise-jet.",
        },
        {
          title: "Conformité stricte aux cotes OEM",
          text: "Tolérances rigoureuses sur diamètres, formes haricot et bandes défilantes pour une pose sans blocage dans les changeurs.",
        },
      ],
    },
    related: {
      heading: "Pages associées et produits recommandés",
      intro: "Explorez nos solutions complémentaires en toiles métalliques inox pour l'industrie plastique.",
      cards: [
        {
          title: "Filtration pour recyclage plastique",
          text: "Toiles et packs de filtration continue pour la granulation et l'extrusion de thermoplastiques recyclés.",
          to: "/PlasticRecyclingFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Toile de filtration et grille de support pour extrusion",
          },
          badge: "Application connexe",
          linkLabel: "Voir le recyclage",
        },
        {
          title: "Pôle filtration des polymères",
          text: "Solutions globales de filtration pour polymères vierges, filage synthétique, extrusion et compoundage.",
          to: "/PolymerFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-polymer-ext-65ec06.png",
            alt: "Pack multicouche pour extrusion de polymères",
          },
          badge: "Pôle principal",
          linkLabel: "Voir les polymères",
        },
        {
          title: "Packs de toiles filtrantes",
          text: "Packs soudés par points et cerclés aluminium pour changeurs de filtres continus sous fortes pressions.",
          to: "/ScreenPacksPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Pack filtrant multicouche pour extrusion",
          },
          badge: "Produit recommandé",
          linkLabel: "Voir les packs filtrants",
        },
        {
          title: "Toiles métalliques tissées",
          text: "Tissages hollandais unis, sergés et toiles carrées calibrées offrant seuil de coupure net et résistance mécanique.",
          to: "/WovenWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Toile métallique tissage hollandais uni",
          },
          badge: "Produit recommandé",
          linkLabel: "Voir les toiles tissées",
        },
        {
          title: "Toiles métalliques frittées",
          text: "Stratifiés frittés multicouches rigides sans migration de fils sous contrainte thermique et forte pression.",
          to: "/SinteredWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disque laminé fritté multicouche pour polymère fondu",
          },
          badge: "Produit recommandé",
          linkLabel: "Voir les toiles frittées",
        },
        {
          title: "Composants filtrants",
          text: "Disques découpés, éléments cylindriques et assemblages spéciaux usinés selon spécifications des extrudeuses.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Composants filtrants en acier inoxydable",
          },
          badge: "Catégorie produit",
          linkLabel: "Voir les composants",
        },
      ],
    },
    cta: {
      id: "polymer-melt-quote",
      eyebrow: "Fabrication de toiles pour polymères fondus",
      heading: "Demandez une offre technique pour toiles de filtration de fondus",
      lead: "Précisez votre type de résine, finesse recherchée, modèle de changeur et dimensions pour recevoir notre proposition technique.",
      secondaryLabel: "Retour filtration des polymères",
      secondaryTo: "/PolymerFiltrationPage",
      note: "Certificats matière (MTR) et contrôle PMI disponibles sur demande lors de votre commande.",
    },
  },
  pt: {
    breadcrumbKey: "PolymerMeltFiltrationApplications",
    relatedTopic: "polymer",
    hero: {
      categoryBadge: "Filtração de polímeros",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Filtração de polímero fundido",
      lead: "Conjuntos de telas multicamadas em aço inoxidável, laminados sinterizados e esteiras de troca contínua desenvolvidos para extrusão de polímeros fundidos de alta viscosidade, fiação e peletização.",
      secondaryCta: {
        label: "Hub de filtração de polímeros",
        to: "/PolymerFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-15b8ef.png",
        alt: "Conjunto hidráulico de trocador de telas para polímero fundido",
      },
      cardNote: "Meios para trocadores de telas",
      cardLink: {
        label: "Especificações técnicas",
        to: "/PolymerMeltFiltrationApplicationsPage#specifications",
      },
      specs: [
        { label: "Grau de filtração", value: "3–250 µm" },
        { label: "Liga metálica", value: "AISI 304L / 316L" },
        { label: "Formatos de entrega", value: "Telas, discos e fitas contínuas" },
        { label: "Tipos de trama", value: "Tramas holandesas e telas sinterizadas" },
      ],
    },
    overview: {
      eyebrow: "Filtração de polímeros de alta viscosidade",
      heading: "Retenção contínua de géis e partículas em correntes de polímero fundido",
      paragraphs: [
        "No processamento contínuo de polímeros, resinas virgens e aditivos concentrados trazem géis não fundidos, pontos de carbono e impurezas degradadas. Sob altas pressões diferenciais, telas simples correm risco de deformação de abertura e rompimento contra a placa de suporte.",
        "A MutualFilter produz meios filtrantes metálicos em aços inoxidáveis AISI 304L e 316L. Tramas holandesas lisas, sarjas e laminados sinterizados multicamadas proporcionam geometria de poros estável, permeabilidade uniforme e elevada rigidez mecânica.",
        "Seja em trocadores de tela tipo gaveta, pistão duplo hidráulico ou sistemas de esteira automática contínua, nossos pacotes soldados por ponto, discos com borda de alumínio e bobinas integram-se perfeitamente à placa de quebra de fluxo para proteger fieiras e matrizes.",
      ],
      keyPoints: [
        "Construção rígida em aço inoxidável que evita deflexão e rompimento sob altas pressões diferenciais sustentadas.",
        "Tramas holandesas lisas, sarjas e laminados sinterizados calibrados que protegem fieiras, matrizes e peletizadores.",
        "Conjuntos emoldurados sob medida, telas soldadas por ponto e esteiras para trocadores contínuos conforme tolerâncias OEM.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/polymer_extrusion_filter.png",
        alt: "Pacote de tela filtrante multicamadas em aço inoxidável para extrusão de polímeros",
      },
    },
    specTable: {
      heading: "Configurações de meios filtrantes para polímeros",
      intro: "Construções padronizadas de telas metálicas aplicadas em filtração de polímeros fundidos. Fabricação sob medida conforme desenho.",
      columns: [
        { key: "configuration", label: "Construção do meio", align: "left" },
        { key: "filtrationRating", label: "Grau de filtração", align: "right" },
        { key: "structure", label: "Estrutura", align: "left" },
        { key: "commonFormats", label: "Formatos disponíveis", align: "left" },
        { key: "primaryUse", label: "Aplicação principal", align: "left" },
      ],
      rows: [
        {
          id: "pm-fine-dutch-pt",
          configuration: "Trama holandesa sarja",
          filtrationRating: "3–25 µm",
          structure: "Tela tecida com microaberturas calibradas",
          commonFormats: "Pacotes soldados, discos com borda",
          primaryUse: "Fiação de fibras sintéticas, filme BOPP",
        },
        {
          id: "pm-plain-dutch-pt",
          configuration: "Trama holandesa lisa",
          filtrationRating: "20–100 µm",
          structure: "Trama compacta de alta densidade",
          commonFormats: "Discos circulares, esteiras contínuas",
          primaryUse: "Extrusão de chapas, linhas de granulação",
        },
        {
          id: "pm-sintered-pack-pt",
          configuration: "Laminado sinterizado 5 camadas",
          filtrationRating: "5–100 µm",
          structure: "Capas unidas por difusão térmica sem migração",
          commonFormats: "Discos emoldurados, velas filtrantes",
          primaryUse: "Linhas contínuas sob alta pressão diferencial",
        },
        {
          id: "pm-multi-stack-pt",
          configuration: "Pacote multicamadas graduado",
          filtrationRating: "25–250 µm",
          structure: "Malha suporte grossa + camada fina de controle",
          commonFormats: "Discos com borda de alumínio, telas rim",
          primaryUse: "Trocadores de telas hidráulicos tipo gaveta",
        },
      ],
      caption: "Nota: Opções em ligas AISI 304, 304L, 316 e 316L adaptadas às dimensões da placa perfurada e ao polímero processado.",
    },
    features: {
      heading: "Diferenciais técnicos para extrusão de polímeros",
      intro: "Meios filtrantes projetados para condições reológicas severas, ciclos de trabalho contínuos e estanqueidade periférica.",
      items: [
        {
          title: "Zero migração de arames",
          text: "A sinterização por difusão e a soldagem a ponto travam os cruzamentos, evitando desfiamento e penetração nos furos de suporte.",
        },
        {
          title: "Corte de retenção uniforme",
          text: "Padrões calibrados de abertura asseguram retenção estável de géis e partículas sem picos imprevisíveis de contrapressão.",
        },
        {
          title: "Vedação lateral rigorosa",
          text: "Bordas em U de alumínio ou cobre garantem assentamento uniforme e impedem desvios ou vazamento de polímero fundido.",
        },
        {
          title: "Conformidade com padrões OEM",
          text: "Tolerâncias precisas em diâmetros, formatos especiais e larguras de fitas contínuas para trocadores de telas comerciais.",
        },
      ],
    },
    related: {
      heading: "Páginas relacionadas e produtos recomendados",
      intro: "Conheça outras aplicações de polímeros e componentes conformados em telas de aço inoxidável.",
      cards: [
        {
          title: "Filtração para reciclagem de plásticos",
          text: "Telas de filtração contínua de polímero fundido para granulação de plásticos reciclados pós-consumo e industriais.",
          to: "/PlasticRecyclingFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Tela de filtração e placa de quebra de fluxo",
          },
          badge: "Aplicação irmã",
          linkLabel: "Ver reciclagem de plásticos",
        },
        {
          title: "Hub de filtração de polímeros",
          text: "Soluções completas de filtração para resinas virgens, fiação sintética, extrusão de filmes e compounding.",
          to: "/PolymerFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-polymer-ext-65ec06.png",
            alt: "Pacote multicamadas para extrusão de polímeros",
          },
          badge: "Hub principal",
          linkLabel: "Ver polímeros",
        },
        {
          title: "Pacotes de telas filtrantes",
          text: "Telas soldadas por ponto e emolduradas em alumínio para trocadores hidráulicos contínuos e altas pressões.",
          to: "/ScreenPacksPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Pacote de tela multicamadas para extrusão",
          },
          badge: "Produto recomendado",
          linkLabel: "Ver pacotes de telas",
        },
        {
          title: "Telas de arame tecidas",
          text: "Tramas holandesas lisas, sarjas e malhas quadradas de alta densidade com corte de partículas definido e rigidez mecânica.",
          to: "/WovenWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Tela de arame inox de trama holandesa lisa",
          },
          badge: "Produto recomendado",
          linkLabel: "Ver telas tecidas",
        },
        {
          title: "Telas de arame sinterizadas",
          text: "Laminados sinterizados multicamadas sem migração de arames sob estresse térmico cíclico e alta pressão.",
          to: "/SinteredWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disco laminado sinterizado multicamadas para polímero fundido",
          },
          badge: "Produto recomendado",
          linkLabel: "Ver telas sinterizadas",
        },
        {
          title: "Componentes filtrantes",
          text: "Discos estampados, cartuchos cilíndricos e conjuntos de telas fabricados conforme especificações OEM.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Componentes filtrantes em aço inoxidável",
          },
          badge: "Categoria de produto",
          linkLabel: "Ver componentes",
        },
      ],
    },
    cta: {
      id: "polymer-melt-quote",
      eyebrow: "Engenharia de telas para polímero fundido",
      heading: "Solicite uma cotação técnica para filtração de polímeros fundidos",
      lead: "Informe o tipo de resina, retenção pretendida, modelo de trocador de telas e dimensões para uma recomendação técnica detalhada.",
      secondaryLabel: "Voltar a filtração de polímeros",
      secondaryTo: "/PolymerFiltrationPage",
      note: "Relatórios de teste de material (MTR) e verificação de liga PMI disponíveis mediante solicitação no pedido.",
    },
  },
};
